import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from 'lucide-react';

const UnifiedFeedback = ({ 
  isVisible, 
  type, // 'loading', 'success', 'error'
  title, 
  message, 
  onClose,
  autoClose = true,
  autoCloseDelay = 3000 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      
      // Auto close for success/error states
      if (autoClose && (type === 'success' || type === 'error')) {
        const timer = setTimeout(() => {
          handleClose();
        }, autoCloseDelay);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, type, autoClose, autoCloseDelay]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      if (onClose) onClose();
    }, 300); // Wait for exit animation
  };

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'loading':
        return (
          <div className="relative">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        );
      case 'success':
        return (
          <div className="relative">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center animate-pulse">
              <CheckCircle className="w-10 h-10 text-green-600 animate-bounce" />
            </div>
            {/* Success ring animation */}
            <div className="absolute inset-0 border-4 border-green-200 rounded-full animate-ping"></div>
          </div>
        );
      case 'error':
        return (
          <div className="relative">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center animate-pulse">
              <XCircle className="w-10 h-10 text-red-600 animate-bounce" />
            </div>
            {/* Error ring animation */}
            <div className="absolute inset-0 border-4 border-red-200 rounded-full animate-ping"></div>
          </div>
        );
      default:
        return null;
    }
  };

  const getColors = () => {
    switch (type) {
      case 'loading':
        return {
          bg: 'bg-white',
          border: 'border-blue-200',
          title: 'text-gray-800',
          message: 'text-gray-600'
        };
      case 'success':
        return {
          bg: 'bg-white',
          border: 'border-green-200',
          title: 'text-green-800',
          message: 'text-green-600'
        };
      case 'error':
        return {
          bg: 'bg-white',
          border: 'border-red-200',
          title: 'text-red-800',
          message: 'text-red-600'
        };
      default:
        return {
          bg: 'bg-white',
          border: 'border-gray-200',
          title: 'text-gray-800',
          message: 'text-gray-600'
        };
    }
  };

  const colors = getColors();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-40' : 'bg-opacity-0'
        }`}
        onClick={type !== 'loading' ? handleClose : undefined}
      />
      
      {/* Feedback Modal */}
      <div 
        className={`relative ${colors.bg} ${colors.border} border-2 rounded-2xl shadow-2xl p-8 mx-4 max-w-md w-full transform transition-all duration-300 ${
          isAnimating 
            ? 'scale-100 opacity-100 translate-y-0' 
            : 'scale-95 opacity-0 translate-y-4'
        }`}
        style={{
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)'
        }}
      >
        {/* Close button for non-loading states */}
        {type !== 'loading' && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <XCircle className="w-5 h-5" />
          </button>
        )}

        {/* Content */}
        <div className="text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            {getIcon()}
          </div>

          {/* Title */}
          {title && (
            <h3 className={`text-xl font-semibold mb-3 ${colors.title}`}>
              {title}
            </h3>
          )}

          {/* Message */}
          {message && (
            <p className={`text-sm leading-relaxed ${colors.message}`}>
              {message}
            </p>
          )}

          {/* Loading dots animation */}
          {type === 'loading' && (
            <div className="flex justify-center mt-4 space-x-1">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnifiedFeedback;
